import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabaseClient';

/**
 * useTypingIndicator(scope, channelId, me)
 *
 * Hook for ephemeral typing indicators using Supabase Realtime broadcast.
 * Broadcasts a "typing" event when notifyTyping() is called (throttled to
 * once per 2.5s) and tracks remote typers in a Map keyed by user id, each
 * with a 4s sliding timeout that auto-clears them.
 *
 * Returns:
 *   typers       — Array<{ id, name }> currently typing (excluding self)
 *   notifyTyping — () => void, call on each keystroke
 *
 * `scope` lets you namespace the channel so portal threads and team
 * channels can't collide even if they share an id.
 */
export default function useTypingIndicator(scope, channelId, me) {
  const [typers, setTypers] = useState([]);
  const lastSentRef     = useRef(0);
  const presenceRef     = useRef(null);
  const timeoutsRef     = useRef(new Map()); // userId → timeout handle

  useEffect(() => {
    if (!channelId || !me?.id) return undefined;

    // Copy the ref's current Map into a local so the cleanup function
    // operates on the same instance even if the ref is replaced later.
    const timeouts = timeoutsRef.current;

    const ch = supabase.channel(`typing:${scope}:${channelId}`, {
      config: { broadcast: { self: false } },
    });

    ch.on('broadcast', { event: 'typing' }, (msg) => {
      const p = msg.payload || {};
      if (!p.user_id || p.user_id === me.id) return;
      setTypers((prev) => {
        if (prev.some((t) => t.id === p.user_id)) return prev;
        return [...prev, { id: p.user_id, name: p.name || 'Someone' }];
      });
      // Reset / start the auto-clear timer for this user.
      const existing = timeoutsRef.current.get(p.user_id);
      if (existing) clearTimeout(existing);
      const handle = setTimeout(() => {
        setTypers((prev) => prev.filter((t) => t.id !== p.user_id));
        timeoutsRef.current.delete(p.user_id);
      }, 4000);
      timeoutsRef.current.set(p.user_id, handle);
    });

    ch.subscribe();
    presenceRef.current = ch;

    return () => {
      for (const t of timeouts.values()) clearTimeout(t);
      timeouts.clear();
      supabase.removeChannel(ch);
      presenceRef.current = null;
    };
  }, [scope, channelId, me?.id]);

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastSentRef.current < 2500) return; // throttle
    lastSentRef.current = now;
    presenceRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        user_id: me?.id,
        name:    me?.name,
      },
    });
  }, [me?.id, me?.name]);

  return { typers, notifyTyping };
}
